import * as S from './RecommendationSlider.styles.js';
import { type CarouselSettings } from '@/components/Carousel/types.js';
import { LongerArrowLeftIcon } from '@/components/Icons/LongerArrowLeftIcon.js';
import { LongerArrowRightIcon } from '@/components/Icons/LongerArrowRightIcon.js';
import { ZodiakQuoteIcon } from '@/components/Icons/ZodiakQuoteIcon.js';
import { type RecommendationNode } from '@/components/Recommendation/types.js';
import { useMatchMedia } from '@/hooks/useMatchMedia.js';
import { minWidthMediaQueries } from '@/stitches/media.js';
import { useCallback, useRef } from 'react';
import type SlickSlider from 'react-slick';

type RecommendationSliderProps = {
  readonly items: RecommendationNode[];
};

const settings: CarouselSettings = {
  adaptiveHeight: true,
  arrows: true,
  cssEase: 'linear',
  cursorPointer: true,
  infinite: true,
  nextArrow: <LongerArrowRightIcon aria-label="Next Slide" />,
  prevArrow: <LongerArrowLeftIcon aria-label="Previous Slide" />,
  responsive: [
    {
      breakpoint: 768,
      settings: {
        arrows: false,
      },
    },
  ],
  slidesToScroll: 1,
  slidesToShow: 1,
  swipeToSlide: true,
  vertical: false,
};

export const RecommendationSlider = ({ items }: RecommendationSliderProps) => {
  const sliderRef = useRef<SlickSlider | null>(null);
  const isMediumViewport = useMatchMedia(minWidthMediaQueries.bp2);

  const nextSlide = useCallback(() => {
    sliderRef.current?.slickNext();
  }, []);

  const previousSlide = useCallback(() => {
    sliderRef.current?.slickPrev();
  }, []);

  return (
    <S.Container justifyTrack={items.length > 1 ? 'start' : 'center'}>
      <S.StyledCarousel
        ref={sliderRef}
        settings={{
          ...settings,
          adaptiveHeight: !isMediumViewport,
          autoplay: items.length >= 1,
          centerMode: true,
          fade: true,
        }}
      >
        {items.map((node) => (
          <S.StyledRecommendationCard
            customQuotationMark={<ZodiakQuoteIcon />}
            customSeparator=" • "
            key={node.id}
            node={node}
            variant="primary"
            withAvatar={false}
          />
        ))}
      </S.StyledCarousel>
      <S.ControlsContainer hide={items.length <= 1}>
        <S.Controls>
          <S.Arrow onClick={previousSlide}>
            <LongerArrowLeftIcon
              aria-label="Previous Slide"
              height={26}
              width={48}
            />
          </S.Arrow>
          <S.Arrow onClick={nextSlide}>
            <LongerArrowRightIcon
              aria-label="Next Slide"
              height={26}
              width={48}
            />
          </S.Arrow>
        </S.Controls>
      </S.ControlsContainer>
    </S.Container>
  );
};
